import React, { FunctionComponent, useCallback, useMemo } from 'react';
import { StateValues } from 'react-use-form-state';
import {
  AdminApi,
  AdminApiConnection,
  AdminApiConnectionsListTypeEnum,
  AdminApiConnectionsPartialUpdateRequest,
  AdminApiConnectionUpdate,
} from 'api';
import useApiCall from 'hooks/use-api-call';
import Translate from 'ui/atoms/translate';
import { Modal, ModalHeader, ModalContent, ModalFooter, ModalFooterButtons, makeModalForm } from 'ui/molecules/modal';
import Tooltip, { TooltipPosition } from 'src/ui/atoms/tooltip';
import Icon from 'src/ui/atoms/icon';
import useIsMedia, { DEVICES } from 'src/ui/hooks/use-is-media';
import * as Styled from './styled';
import { DistributionPlatformDropdownOption } from '..';
import Spacer from 'ui/atoms/spacer';

interface EditApiConnectionModalProps {
  onSaveApiConnection: (editedApiConnection: AdminApiConnectionUpdate, regenerateClientSecret: boolean) => void;
  onClose: () => void;
  apiConnection: AdminApiConnection;
  distributionPlatforms: DistributionPlatformDropdownOption[];
}

interface EditApiConnectionFields {
  name: string;
  type: AdminApiConnectionsListTypeEnum;
  distributionPlatform: string;
  regenerateClientSecret: boolean;
}

export const Form = makeModalForm<EditApiConnectionFields>();

const EditApiConnectionModal: FunctionComponent<EditApiConnectionModalProps> = ({
  onSaveApiConnection,
  onClose,
  apiConnection,
  distributionPlatforms,
}) => {
  const { makeAuthenticatedApi, withApi, error } = useApiCall();

  const adminApi: AdminApi = useMemo(() => makeAuthenticatedApi(AdminApi), [makeAuthenticatedApi]);

  const isPhone = useIsMedia(DEVICES.phone);

  const onSubmit = useCallback((fieldsValues: StateValues<EditApiConnectionFields>) => {
    withApi(async () => {
      const params: AdminApiConnectionsPartialUpdateRequest = {
        id: apiConnection.id,
        patchedAdminApiConnectionUpdateRequest: {
          name: fieldsValues.name,
          regenerateClientSecret: fieldsValues.regenerateClientSecret,
        },
      };
      const response = await adminApi.adminApiConnectionsPartialUpdate(params);
      onSaveApiConnection(response, fieldsValues.regenerateClientSecret);
    });
  }, []);

  return (
    <Modal onClose={onClose}>
      <Form
        i18nKey="addApiConnectionModal"
        initial={{
          name: apiConnection?.name,
          type: apiConnection?.apiType,
          distributionPlatform: apiConnection?.distributionPlatform ?? undefined,
        }}
        onSubmit={(values: StateValues<EditApiConnectionFields>) => {
          onSubmit(values);
        }}
        error={error}
      >
        <ModalHeader>
          <Translate name={'editApiConnectionModal.title'} />
        </ModalHeader>
        <ModalContent>
          <Form.ValueProvider>
            {({ type }) => (
              <>
                <Form.Group
                  name="name"
                  required={true}
                  requiredError={<Translate name="addApiConnectionModal.fields.name.requiredError" />}
                >
                  <Form.Input />
                </Form.Group>
                <Form.Group name="type" required>
                  <Form.GroupToggle>
                    <Form.Radio value={AdminApiConnectionsListTypeEnum.ISSUER_API} disabled>
                      <Translate name={'issuerUserApiConnectionsTable.issuerApi'} />
                    </Form.Radio>
                    <Form.Radio value={AdminApiConnectionsListTypeEnum.DISTRIBUTOR_API} disabled>
                      <Translate name={'issuerUserApiConnectionsTable.distributorApi'} />
                    </Form.Radio>
                  </Form.GroupToggle>
                </Form.Group>
                {type === AdminApiConnectionsListTypeEnum.DISTRIBUTOR_API && (
                  <Form.Group
                    name="distributionPlatform"
                    required={true}
                    requiredError={<Translate name="addApiConnectionModal.fields.distributionPlatform.requiredError" />}
                  >
                    <Form.Select
                      options={distributionPlatforms.map(({ value, label }) => ({
                        value: value,
                        label: label,
                      }))}
                      disabled
                    ></Form.Select>
                  </Form.Group>
                )}
                <Form.Group name="regenerateClientSecret" label={null} spacing="small">
                  <Form.Checkbox>
                    <Styled.CheckboxRow>
                      <Translate name="editApiConnectionModal.regenerateClientSecret" />
                      <Spacer x={2} />
                      <Tooltip
                        content={<Translate name="editApiConnectionModal.regenerateClientSecretTooltip" />}
                        position={isPhone ? TooltipPosition.BOTTOM : TooltipPosition.RIGHT}
                      >
                        <Icon name="question" circular={true} size="large" padded="none" color="info" />
                      </Tooltip>
                    </Styled.CheckboxRow>
                  </Form.Checkbox>
                </Form.Group>
              </>
            )}
          </Form.ValueProvider>
        </ModalContent>
        <ModalFooter>
          <ModalFooterButtons
            actionButtons={[
              {
                name: 'cancel',
                content: <Translate name="common.cancel" />,
                size: 'large',
                onClick: () => onClose(),
              },
              {
                name: 'send',
                content: <Translate name="common.saveChanges" />,
                variant: 'primary',
                type: 'submit',
                size: 'large',
              },
            ]}
          />
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default EditApiConnectionModal;
